const getDealsDefaultState = () => {
  return {
    // Dashboard / Deal Builder
    deals: [],
    dealsLoaded: false,
    current: null, // the deal open in the builder, null when the builder is closed
    picker: {
      // product / size / store options, fetched once and reused by the builder
      categories: {},
      // store specials by their menu ({ "Store Speciality": ["2 Slices & Pop", ...] }), for tap links
      specials: {},
      menuCategories: [],
      sizes: [],
      sizeCodes: [],
      stores: [],
      storeHash: "",
      scopedStore: "",
      loaded: false,
    },

    // Rule Builder
    targetingDeals: [],

    // Intelligence
    proposals: [],
    proposalFilter: "pending",

    // Analytics
    arms: [],
  };
};

/**
 * Deal Engine data. Talks to the /deals group on the brand's own API, which backs
 * the same mc_deals rows the mobile apps read — a change here is live for
 * customers as soon as the deal is switched on.
 *
 * Non-namespaced like every other module here, and POSTs form-urlencoded
 * because the API only allows the Authorization header through CORS.
 */
const deals = {
  state: getDealsDefaultState(),

  mutations: {
    RESET_DEALS(state) {
      Object.assign(state, getDealsDefaultState());
    },
    SET_DEALS(state, payload) {
      state.deals = Array.isArray(payload) ? payload : [];
      state.dealsLoaded = true;
    },
    SET_DEAL_PICKER(state, payload) {
      state.picker = {
        categories: payload.categories || {},
        specials: payload.specials || {},
        menuCategories: payload.menu_categories || [],
        sizes: payload.sizes || [],
        sizeCodes: payload.size_codes || [],
        stores: payload.stores || [],
        storeHash: payload.storeHash || "",
        scopedStore: payload.scoped_store || "",
        loaded: true,
      };
    },
    SET_CURRENT_DEAL(state, payload) {
      state.current = payload;
    },
    // The API returns enabled as a STRING ("0"/"1") because the PDO handle runs
    // with STRINGIFY_FETCHES, so compare loosely everywhere it is read.
    SET_DEAL_ENABLED(state, { id, enabled }) {
      const row = state.deals.find((d) => String(d.id) === String(id));
      if (row) row.enabled = String(enabled);
    },
    REMOVE_DEAL(state, id) {
      state.deals = state.deals.filter((d) => String(d.id) !== String(id));
    },
    SET_TARGETING_DEALS(state, payload) {
      state.targetingDeals = Array.isArray(payload) ? payload : [];
    },
    SET_DEAL_TARGETING(state, { id, segments, tiers }) {
      const row = state.targetingDeals.find((d) => String(d.id) === String(id));
      if (row) {
        row.target_segments = segments || null;
        row.target_tiers = tiers || null;
      }
    },
    SET_PROPOSALS(state, payload) {
      state.proposals = Array.isArray(payload) ? payload : [];
    },
    SET_PROPOSAL_FILTER(state, payload) {
      state.proposalFilter = payload;
    },
    SET_BANDIT_ARMS(state, payload) {
      state.arms = Array.isArray(payload) ? payload : [];
    },
  },

  actions: {
    // ---- Dashboard / Builder -------------------------------------------

    async getDeals({ commit }) {
      commit("LOADING", { isActiveStatus: true });
      try {
        const { data } = await window.$http.axios.get("deals/list");
        if (data.status) commit("SET_DEALS", data.deals);
        else notifyError(commit, data.message);
      } catch (error) {
        notifyError(commit, "Could not load deals.");
      }
      commit("LOADING", { isActiveStatus: false });
    },

    // Scoped to the store selected in the header. Without a store the list
    // carries every store's copy of the same pizza — 452 products with ten
    // "Pepperoni" entries and nothing to tell them apart. Scoped it is ~80 with
    // one of each. Saving still covers every store; the API expands the chosen
    // pHash to its siblings.
    // Under "All stores" the brand's first store stands in: its menu names the items, and saving matches them at
    // the other stores just the same.
    async getDealPicker({ state, commit, rootState, rootGetters }, force) {
      const current = rootState.stores?.current || {};
      const storeHash = current.storeHash || (current.allStores && rootGetters.brandStores[0]?.shash) || "";
      if (state.picker.loaded && state.picker.storeHash === storeHash && !force) return;
      try {
        const { data } = await window.$http.axios.get("deals/products", {
          params: storeHash ? { s: storeHash } : {},
        });
        if (data.status) commit("SET_DEAL_PICKER", { ...data, storeHash });
      } catch (error) {
        notifyError(commit, "Could not load the product list.");
      }
    },

    async getDeal({ commit }, dealId) {
      commit("LOADING", { isActiveStatus: true });
      try {
        const { data } = await window.$http.axios.get("deals/get", {
          params: { deal_id: dealId },
        });
        if (data.status) commit("SET_CURRENT_DEAL", data.deal);
        else notifyError(commit, data.message);
      } catch (error) {
        notifyError(commit, "Could not load the deal.");
      }
      commit("LOADING", { isActiveStatus: false });
    },

    async saveDeal({ commit, dispatch }, deal) {
      commit("LOADING", { isActiveStatus: true });
      const isUpdate = !!deal.deal_id;
      const params = new URLSearchParams();
      if (isUpdate) params.append("deal_id", deal.deal_id);
      params.append("deal_key", deal.deal_key || "");
      params.append("title", deal.title || "");
      params.append("subtitle", deal.subtitle || "");
      params.append("badge", deal.badge || "");
      params.append("type", deal.type || "");
      params.append("style", deal.style || "gold");
      params.append("display_priority", deal.display_priority ?? 100);
      params.append("hero_image_url", deal.hero_image_url || "");
      params.append("banner_enabled", deal.banner_enabled ? 1 : 0);
      params.append("banner_icon", deal.banner_icon || "");
      params.append("banner_cta", deal.banner_cta || "");
      params.append("banner_target_phash", deal.banner_target_phash || "");
      params.append("banner_priority", deal.banner_priority ?? 100);
      // Structured fields travel as JSON strings — see the module doc comment.
      params.append("rules", JSON.stringify(deal.rules || {}));
      params.append("combo_slots", JSON.stringify(deal.combo_slots || []));
      // Schedule. Always sent: a blank value clears the field on the server,
      // which is how an end date gets removed again.
      params.append("starts_at", deal.starts_at || "");
      params.append("ends_at", deal.ends_at || "");
      params.append("active_days", deal.active_days || "");
      params.append("active_from", deal.active_from || "");
      params.append("active_until", deal.active_until || "");

      let ok = false;
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: isUpdate ? "deals/update" : "deals/add",
          data: params,
        });
        if (data.status) {
          ok = true;
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: "Saved",
            // A new deal is created switched OFF on purpose; say so, otherwise
            // the operator assumes it is already running.
            message: isUpdate
              ? "Deal has been updated."
              : "Deal created — it is OFF until you switch it on.",
          });
          await dispatch("getDeals");
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not save the deal.");
      }
      commit("LOADING", { isActiveStatus: false });
      return ok;
    },

    // Deal Builder picture: the file goes to deals/upload-hero, which stores it
    // under www/images/deals/ and answers with the public URL. The URL is then
    // saved in hero_image_url exactly as before, so the apps need no change.
    // Native FormData: axios drops the urlencoded default header for it and
    // lets the browser set the multipart boundary.
    async uploadDealHeroImage({ commit }, file) {
      const formData = new FormData();
      formData.append("image", file, file.name);
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/upload-hero",
          data: formData,
        });
        if (data.status && data.url) return data.url;
        notifyError(commit, data.message || "The picture could not be uploaded.");
      } catch (error) {
        notifyError(commit, "The picture could not be uploaded.");
      }
      return "";
    },

    async toggleDeal({ commit }, { id, enabled }) {
      const params = new URLSearchParams();
      params.append("deal_id", id);
      params.append("enabled", enabled ? 1 : 0);
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/toggle",
          data: params,
        });
        if (data.status) {
          commit("SET_DEAL_ENABLED", { id, enabled: data.enabled });
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: enabled ? "Live" : "Paused",
            message: enabled
              ? "Deal is now visible to customers."
              : "Deal is no longer offered.",
          });
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not change the deal status.");
      }
    },

    async deleteDeal({ commit }, id) {
      const params = new URLSearchParams();
      params.append("deal_id", id);
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/delete",
          data: params,
        });
        if (data.status) {
          commit("REMOVE_DEAL", id);
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: "Deleted",
            message: "Deal has been removed.",
          });
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not delete the deal.");
      }
    },

    // ---- Rule Builder ---------------------------------------------------

    async getTargetingDeals({ commit }) {
      commit("LOADING", { isActiveStatus: true });
      try {
        const { data } = await window.$http.axios.get("deals/targeting");
        if (data.status) commit("SET_TARGETING_DEALS", data.deals);
        else notifyError(commit, data.message);
      } catch (error) {
        notifyError(commit, "Could not load targeting.");
      }
      commit("LOADING", { isActiveStatus: false });
    },

    async saveTargeting({ commit }, { id, segments, tiers }) {
      const params = new URLSearchParams();
      params.append("id", id);
      params.append("target_segments", segments || "");
      params.append("target_tiers", tiers || "");
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/targeting-save",
          data: params,
        });
        if (data.status) {
          commit("SET_DEAL_TARGETING", { id, segments, tiers });
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: "Saved",
            message: "Targeting has been updated.",
          });
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not save targeting.");
      }
    },

    // ---- Intelligence ---------------------------------------------------

    async getProposals({ state, commit }) {
      commit("LOADING", { isActiveStatus: true });
      try {
        const { data } = await window.$http.axios.get("deals/proposals", {
          params: { status: state.proposalFilter || "", limit: 100 },
        });
        if (data.status) commit("SET_PROPOSALS", data.proposals);
        else notifyError(commit, data.message);
      } catch (error) {
        notifyError(commit, "Could not load proposals.");
      }
      commit("LOADING", { isActiveStatus: false });
    },

    async approveProposal({ commit, dispatch }, id) {
      const params = new URLSearchParams();
      params.append("id", id);
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/proposal-approve",
          data: params,
        });
        if (data.status) {
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: "Approved",
            // Approving means "reviewed and correct", not "charge people this".
            message: "Deal created and left OFF — switch it on when you are ready.",
          });
          await dispatch("getProposals");
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not approve the proposal.");
      }
    },

    async rejectProposal({ commit, dispatch }, { id, reason }) {
      const params = new URLSearchParams();
      params.append("id", id);
      params.append("reason", reason || "");
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/proposal-reject",
          data: params,
        });
        if (data.status) {
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: "Rejected",
            message: "Proposal has been dismissed.",
          });
          await dispatch("getProposals");
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not reject the proposal.");
      }
    },

    async toggleProposalDeal({ commit, dispatch }, { id, enabled }) {
      const params = new URLSearchParams();
      params.append("id", id);
      params.append("enabled", enabled ? 1 : 0);
      try {
        const { data } = await window.$http.axios({
          method: "post",
          url: "deals/proposal-toggle",
          data: params,
        });
        if (data.status) {
          commit("NOTIFICATION_MESSAGE", {
            status: 1,
            title: enabled ? "Live" : "Paused",
            message: enabled
              ? "Deal is now visible to customers."
              : "Deal is no longer offered.",
          });
          await dispatch("getProposals");
        } else {
          notifyError(commit, data.message);
        }
      } catch (error) {
        notifyError(commit, "Could not change the deal status.");
      }
    },

    // ---- Analytics ------------------------------------------------------

    async getBanditArms({ commit }) {
      commit("LOADING", { isActiveStatus: true });
      try {
        const { data } = await window.$http.axios.get("deals/bandit-arms");
        if (data.status) commit("SET_BANDIT_ARMS", data.arms);
        else notifyError(commit, data.message);
      } catch (error) {
        notifyError(commit, "Could not load performance data.");
      }
      commit("LOADING", { isActiveStatus: false });
    },
  },
};

function notifyError(commit, message) {
  commit("NOTIFICATION_MESSAGE", {
    status: -1,
    title: "Error",
    message: message || "Something went wrong.",
  });
}

export default deals;
